import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Edit, Plus, RefreshCw, Settings } from 'lucide-react';
import { icons, LucideIcon } from 'lucide-react';
import { useEventTypes } from '@/hooks/useEventTypes';
import { eventTypeService } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import { EventTypeResponse } from '@/types';

type SortColumn = 'code' | 'event';

export const EventTypesManagementPage = () => {
  const navigate = useNavigate();
  const { eventTypes, reloadEventTypes, loading } = useEventTypes();
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [sortField, setSortField] = useState<{ column: SortColumn; direction: 'asc' | 'desc' }>({
    column: 'code',
    direction: 'asc',
  });

  const toggleSort = (column: SortColumn) => {
    setSortField((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  };

  const sorted = useMemo(() => {
    const copy: EventTypeResponse[] = [...eventTypes];
    copy.sort((a, b) => {
      if (sortField.column === 'code') {
        const n = a.code.localeCompare(b.code, undefined, { sensitivity: 'base' });
        return sortField.direction === 'asc' ? n : -n;
      }
      const n = a.labelEs.localeCompare(b.labelEs, 'es', { sensitivity: 'base' });
      return sortField.direction === 'asc' ? n : -n;
    });
    return copy;
  }, [eventTypes, sortField]);

  return (
    <div className="h-full bg-brand-white py-4">
      <LoadingOverlay isVisible={loading || statusUpdating} message="Cargando tipos de eventos..." />

      <div className={`max-w-[100%] mx-auto space-y-6 ${loading || statusUpdating ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <Settings className="h-7 w-7 text-brand-orange" strokeWidth={1.5} />
            <h1 className="text-3xl font-bold text-brand-orange">Gestión de tipos de eventos</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void reloadEventTypes()}
              disabled={loading || statusUpdating}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={() => navigate('/platform/event-types/new')}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Crear nuevo tipo
            </button>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-brand-gray-dark">
                <tr className="text-left text-xs font-medium text-brand-white tracking-wider">
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 select-none hover:text-brand-orange cursor-pointer rounded-sm"
                      onClick={() => toggleSort('code')}
                    >
                      <span>Código</span>
                      {sortField.column === 'code' ? (
                        sortField.direction === 'asc' ? (
                          <ArrowUp className="w-4 h-4 shrink-0" />
                        ) : (
                          <ArrowDown className="w-4 h-4 shrink-0" />
                        )
                      ) : (
                        <ArrowUpDown className="w-4 h-4 shrink-0 opacity-70" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 select-none hover:text-brand-orange cursor-pointer rounded-sm"
                      onClick={() => toggleSort('event')}
                    >
                      <span>Evento</span>
                      {sortField.column === 'event' ? (
                        sortField.direction === 'asc' ? (
                          <ArrowUp className="w-4 h-4 shrink-0" />
                        ) : (
                          <ArrowDown className="w-4 h-4 shrink-0" />
                        )
                      ) : (
                        <ArrowUpDown className="w-4 h-4 shrink-0 opacity-70" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3">Etiqueta ES</th>
                  <th className="px-4 py-3">Etiqueta EN</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark" colSpan={6}>
                      Cargando tipos de eventos...
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark" colSpan={6}>
                      No hay tipos cargados.
                    </td>
                  </tr>
                ) : (
                  sorted.map((item, index) => {
                    const Icon = (icons[item.icon as keyof typeof icons] as LucideIcon) || icons.CircleUserRound;
                    return (
                      <tr
                        key={item.id}
                        className={`border-t border-gray-100 cursor-pointer ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                        } hover:bg-brand-orange hover:bg-opacity-30 transition-colors duration-150`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark font-mono">
                          <div className="flex items-center gap-2">
                            <span>{item.code}</span>
                            {item.autoCreated && (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                Pendiente de configurar
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" style={{ color: item.iconColor }} />
                            <span
                              className="px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: item.bgColor, color: item.textColor }}
                            >
                              {item.labelEs}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark">{item.labelEs}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark">{item.labelEn}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              item.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {item.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-brand-gray-dark">
                          <div className="flex items-center gap-2">
                            <button
                              className="px-3 py-1 rounded-md transition-colors duration-200 sentence-case flex items-center gap-1 bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white"
                              onClick={() => navigate(`/platform/event-types/edit/${item.id}`)}
                            >
                              <Edit className="w-3.5 h-3.5" />
                              Editar
                            </button>
                            <button
                              className="px-3 py-1 rounded-md transition-colors duration-200 sentence-case bg-brand-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-brand-white"
                              onClick={async () => {
                                setStatusUpdating(true);
                                try {
                                  await eventTypeService.setActive(item.id, !item.active);
                                  await reloadEventTypes();
                                } finally {
                                  setStatusUpdating(false);
                                }
                              }}
                            >
                              {item.active ? 'Desactivar' : 'Activar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
